import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  MakeCancelable,
  makeCancelable,
} from 'ui-components/src/utilities/makeCancelable';
import { useLogger } from 'ui-components/src/hooks/useLogger';
import { ConfigContext } from '../ConfigContext';

const AuthTokenContext = createContext<AuthTokenContextValue | null>(null);

export const useAuthTokenContext = () => {
  const context = useContext(AuthTokenContext);

  if (!context)
    throw new Error(
      'useAuthTokenContext can only be used inside AuthTokenContext',
    );

  return context as AuthTokenContextValue;
};

/**
 * Authentication context provider which manages the active JWT token
 * - It exposes the active JWT token and a static getter
 * - It forces the user to login on initialization if he's not
 * - It automatically refreshes the JWT token once it's about to expire
 */
export const AuthTokenProvider: React.FC = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();
  const logger = useLogger();
  const [token, setToken] = useState('');
  const tokenRef = useRef(token);
  const config = ConfigContext.useContainer();

  // Logs the user in if he is not authenticated
  useEffect(() => {
    if (isLoading || isAuthenticated || !loginWithRedirect) {
      return;
    }

    const login = async () => {
      await loginWithRedirect({
        authorizationParams: {
          connection: config.auth0.connection,
        },
        appState: { targetUrl: window.location.pathname },
      });
    };
    const call = makeCancelable(login());

    return () => {
      call.cancel();
    };
  }, [isLoading, isAuthenticated, loginWithRedirect, config.auth0.connection]);

  // Fetches the access token on init
  useEffect(() => {
    async function call() {
      const accessToken = await getAccessTokenSilently();
      tokenRef.current = accessToken;
      setToken(accessToken);
    }

    let callRef: MakeCancelable;
    if (isAuthenticated) callRef = makeCancelable(call());

    return () => {
      if (callRef) callRef.cancel();
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  // Automatically refreshes the access token when it's about to expire
  useEffect(() => {
    let timeout: number;
    async function setup() {
      if (isAuthenticated) {
        const claims = await getIdTokenClaims();
        const delay = (claims?.exp || 60) * 1000 - 10000 - Date.now();

        timeout = window.setTimeout(async () => {
          logger.info('Refreshing access token...');
          const token = await getAccessTokenSilently();
          tokenRef.current = token;
          setToken(token);
        }, delay);
      }
    }
    const callRef = makeCancelable(setup());

    return () => {
      clearTimeout(timeout);
      callRef.cancel();
    };
  }, [isAuthenticated, logger, getIdTokenClaims, getAccessTokenSilently]);

  const value = useMemo(
    () => ({
      getToken: () => tokenRef.current,
    }),
    [],
  );
  return token ? (
    <AuthTokenContext.Provider value={value}>
      {children}
    </AuthTokenContext.Provider>
  ) : null;
};

export interface AuthTokenContextValue {
  getToken: () => string;
}
